import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, AlertTriangle, Crown } from "lucide-react";

/** AI suggestion panel. Suggests only — the player still submits their own entry. */
export const AiCoachPanel = ({ contestId, players, onApply, disabled }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [applied, setApplied] = useState(false);
  const [ent, setEnt] = useState(null);
  const [buying, setBuying] = useState("");

  const loadEnt = () =>
    api.get("/ai/entitlement").then(({ data }) => setEnt(data)).catch(() => {});

  useEffect(() => {
    loadEnt();
  }, []);

  const nameOf = (pid) => players?.find((p) => p.player_id === pid)?.name || pid;

  const run = async () => {
    setLoading(true);
    setError("");
    setLocked(false);
    try {
      const { data } = await api.post("/ai/pick-coach", { contest_id: contestId });
      setResult(data);
      if (data.entitlement) setEnt(data.entitlement);
      else loadEnt();
    } catch (e) {
      const status = e?.response?.status;
      setError(e?.response?.data?.detail || "The AI coach is unavailable right now.");
      if (status === 402) {
        setLocked(true);
        loadEnt();
      }
    } finally {
      setLoading(false);
    }
  };

  const buyPlan = async (plan) => {
    setBuying(plan);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBuying("");
    }
  };

  const apply = () => {
    onApply?.(result.ranking.map((r) => r.player_id));
    setApplied(true);
    setTimeout(() => setApplied(false), 2200);
  };

  return (
    <div className="border border-[#7C3AED]/40 bg-[#7C3AED]/[0.06] p-4" data-testid="ai-coach-panel">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[#C4B5FD] text-xs uppercase tracking-widest">
          <Sparkles size={13} /> AI Pick Coach
        </div>
        {ent && (
          <span
            className="text-[10px] uppercase tracking-widest text-white/50"
            data-testid="ai-coach-quota"
          >
            {ent.pass_active ? (
              <span className="text-[#FFD700] inline-flex items-center gap-1">
                <Crown size={11} /> {ent.sub_active ? "Coach Pro active" : "Coach Pass active"}
              </span>
            ) : (
              `${ent.free_remaining} of ${ent.free_limit} free reads left`
            )}
          </span>
        )}
      </div>

      {!result && !locked && (
        <>
          <button
            onClick={run}
            disabled={loading}
            data-testid="ai-coach-run-btn"
            className="bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5 mb-2"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {loading ? "Reading the slate..." : "Get AI read"}
          </button>
          <p className="text-white/50 text-xs">
            Ranks this pool on projections, recent form and matchups — then you decide.
          </p>
        </>
      )}

      {error && !locked && (
        <div className="flex items-start gap-2 text-[#FF9F0A] text-xs" data-testid="ai-coach-error">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {locked && ent && (
        <div className="border border-[#FFD700]/40 bg-[#FFD700]/[0.06] p-3" data-testid="ai-coach-upsell">
          <div className="inline-flex items-center gap-1.5 text-[#FFD700] text-xs uppercase tracking-widest font-bold mb-1">
            <Crown size={13} /> Unlock unlimited reads
          </div>
          <p className="text-white/70 text-sm mb-1">
            You&apos;ve used all {ent.free_limit} free reads this week. Go unlimited on every slate.
          </p>
          <p className="text-white/40 text-xs mb-3">
            Billed to your card — your gaming wallet stays untouched · free reads reset Monday
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => buyPlan("pass")}
              disabled={!!buying}
              data-testid="ai-coach-buy-pass-btn"
              className="bg-[#FFD700] hover:bg-[#e6c200] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
            >
              {buying === "pass" ? <Loader2 size={13} className="animate-spin" /> : <Crown size={13} />}
              {ent.pass_days}-day pass · ${ent.pass_price.toFixed(2)}
            </button>
            <button
              onClick={() => buyPlan("monthly")}
              disabled={!!buying}
              data-testid="ai-coach-buy-sub-btn"
              className="bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
            >
              {buying === "monthly" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Coach Pro · ${Number(ent.sub_price || 0).toFixed(2)}/mo
            </button>
          </div>
        </div>
      )}

      {result && (
        <>
          <ol className="space-y-2 mb-3" data-testid="ai-coach-ranking">
            {result.ranking.map((r) => (
              <li key={r.player_id} className="flex items-start gap-2.5">
                <span className="font-display text-sm text-[#C4B5FD] w-5 flex-shrink-0">
                  {String(r.rank).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{nameOf(r.player_id)}</div>
                  <div className="text-white/50 text-xs">{r.reason}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={apply}
              disabled={disabled}
              data-testid="ai-coach-apply-btn"
              className="bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
            >
              {applied ? <Check size={13} className="text-[#00FF66]" /> : null}
              {applied ? "Loaded into your slip" : "Use these picks"}
            </button>
            <button
              onClick={() => {
                setResult(null);
                run();
              }}
              data-testid="ai-coach-refresh-btn"
              className="text-white/40 hover:text-white text-xs uppercase tracking-widest"
            >
              Re-read
            </button>
          </div>
          <p className="text-white/30 text-[10px] leading-snug mt-3">{result.disclaimer}</p>
        </>
      )}
    </div>
  );
};
